import axios from "axios";

const AUTH_RELOAD_KEY = "tkc_auth_redirect_at";
const AUTH_RELOAD_COOLDOWN_MS = 10000; // 10 seconds

/**
 * Check if this looks like a redirect to the sign-in page.
 * This can happen when the server returns a 200 with HTML instead of JSON.
 */
const isRedirectToSignIn = (response) => {
  // Check if the response URL indicates a redirect to sign-in
  const responseUrl = response?.request?.responseURL;
  if (
    typeof responseUrl === "string" &&
    responseUrl.includes("/users/sign_in")
  ) {
    return true;
  }

  // Check content-type - if we expected JSON but got HTML, that's suspicious
  const contentType = response?.headers?.["content-type"] || "";
  const isHtmlResponse = contentType.includes("text/html");
  const data = response?.data;

  // If we got HTML and it contains the sign-in form, it's an auth redirect
  if (
    isHtmlResponse &&
    typeof data === "string" &&
    data.includes("sign-in-page__form-block")
  ) {
    return true;
  }

  return false;
};

/**
 * Create a custom error class for auth redirects.
 * This allows downstream catch blocks to identify and handle these specially.
 */
class AuthRedirectError extends Error {
  constructor(message = "Session expired") {
    super(message);
    this.name = "AuthRedirectError";
    this.isAuthRedirect = true;
  }
}

/**
 * Navigate to sign-in page when auth has expired.
 * Uses sessionStorage to prevent reload loops.
 */
const navigateToSignIn = () => {
  if (typeof window === "undefined") return false;
  if (typeof sessionStorage === "undefined") return false;

  // Already on sign-in page, don't redirect
  if (window.location.pathname === "/users/sign_in") return false;

  // Check if we recently triggered an auth redirect to prevent loops
  const lastRedirect = sessionStorage.getItem(AUTH_RELOAD_KEY);
  if (lastRedirect) {
    const elapsed = Date.now() - parseInt(lastRedirect, 10);
    if (elapsed < AUTH_RELOAD_COOLDOWN_MS) {
      // Already redirected recently, don't loop
      return false;
    }
  }

  // Record the redirect timestamp
  sessionStorage.setItem(AUTH_RELOAD_KEY, Date.now().toString());

  // Navigate to sign-in page (more deterministic than reload)
  window.location.assign("/users/sign_in");
  return true;
};

// Set default headers to request JSON responses
axios.defaults.headers.common["Accept"] = "application/json";
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

axios.interceptors.response.use(
  (response) => {
    // Check if we got a sign-in page instead of the expected response
    if (isRedirectToSignIn(response)) {
      navigateToSignIn();
      return Promise.reject(new AuthRedirectError());
    }

    return response;
  },
  (error) => {
    const response = error?.response;

    // Handle 401 responses or sign-in redirects
    if (response?.status === 401 || isRedirectToSignIn(response)) {
      navigateToSignIn();
      // Tag the error so downstream handlers can identify it
      error.isAuthRedirect = true;
    }

    // Enrich error with request context for Honeybadger
    if (error?.config) {
      error.metadata = {
        request_url: error.config.url,
        request_method: error.config.method,
        response_status: error.response?.status,
      };
    }

    return Promise.reject(error);
  },
);

export { AuthRedirectError };
